import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Params, Router } from '@angular/router';
import { ErrorCode } from '@common/http/enums/error-code.enum';
import { CoreService } from '@portal-client/core/core.service';
import { AuthStatusMessage } from '@portal-core/auth/enums/auth-status-message.enum';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { AlertType } from '@portal-core/general/enums/alert-type.enum';
import { RouterService } from '@portal-core/general/services/router.service';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { LicenseActivationType } from '@portal-core/licenses/enums/license-activation-type.enum';
import { LicenseType } from '@portal-core/licenses/enums/license-type.enum';
import { LicensesService } from '@portal-core/licenses/services/licenses.service';
import { catchError, first, map, of, tap } from 'rxjs';

export const authLicenseUserDataGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const coreService = inject(CoreService);
  const errorService = inject(ErrorService);
  const licensesService = inject(LicensesService);
  const licenseUsersService = inject(LicenseUsersService);
  const routerService = inject(RouterService);

  const maybeNavigateToAlertPage = (alertRoute: ActivatedRouteSnapshot, queryParams: Params) => {
    const routePath = routerService.getRoutePath(alertRoute);

    if (routePath.length < 1 || routePath[routePath.length - 1] !== 'alert') {
      router.navigate(['/alert'], { queryParams });
    }
  };

  const cachedAuthLicenseUserData = authService.getAuthLicenseUserData();
  if (cachedAuthLicenseUserData?.License) {
    return of(true);
  }

  return authService.getAuthLicenseUserData$().pipe(
    first(),
    map(authUserData => {
      // Add the licenses and license users to their data stores
      licensesService.addItems$([authUserData.License]);
      licenseUsersService.addItems$([authUserData.LicenseUser]);

      // Set the current items in the core service
      coreService.setLicenseId$(authUserData.License.Id);
      coreService.setLicenseUserId$(authUserData.LicenseUser.Id);

      return authUserData.License;
    }),
    tap(license => {
      // Only redirect to an alert page if not already on the alert page
      if (license.IsDeleted) {
        maybeNavigateToAlertPage(route, { type: AlertType.LicenseNotFound, licenseId: license.Id });
      } else if (license.LicenseActivationStatus === LicenseActivationType.Deactivated) {
        maybeNavigateToAlertPage(route, { type: AlertType.DeactivatedLicense, licenseId: license.Id });
      } else if (license.LicenseActivationStatus === LicenseActivationType.Inactive) {
        if (license.LicenseType === LicenseType.Full) {
          maybeNavigateToAlertPage(route, { type: AlertType.ExpiredLicense, licenseId: license.Id });
        } else {
          maybeNavigateToAlertPage(route, { type: AlertType.ExpiredTrial, licenseId: license.Id });
        }
      } else if (license.LicenseActivationStatus === LicenseActivationType.BillingFailed) {
        maybeNavigateToAlertPage(route, { type: AlertType.BillingFailed, licenseId: license.Id });
      }
    }),
    map(() => true),
    catchError(error => {
      if (error.status === 403) {
        const authStatus: string = AuthStatusMessage[error.headers.get('X-AuthStatus') as keyof typeof AuthStatusMessage];

        // If there is an auth status message AND it is not the NeedsPermission message then this is a drastic auth error
        if (authStatus && authStatus !== AuthStatusMessage.NeedsPermission) {
          maybeNavigateToAlertPage(route, { type: authStatus });
        } else {
          maybeNavigateToAlertPage(route, { type: AlertType.LicenseAccessDenied });
        }
      } else if (error.status === 404) {
        maybeNavigateToAlertPage(route, { type: AlertType.LicenseNotFound });
      } else if (error.status === 400) {
        if (errorService.hasErrorCode(error, ErrorCode.UserNotFoundError)) {
          maybeNavigateToAlertPage(route, { type: AlertType.LicenseAccessDenied });
        }
      } else if (error.status !== 401) { // 401 is handled by authGuard
        maybeNavigateToAlertPage(route, { type: AlertType.AuthInitError });
      }

      return of(true);
    })
  );
};
